// Engine v2 phase-0 scale buttons (spec §3.7), the PWA counterpart of the TG
// inline keyboard: rendered from metadata.engine_scale.points on a reply.
// after_practice carries BOTH rows (1–10 + «Пропустить» for intensity,
// ★1–5 for usefulness) — TG parity, else intensity_after is never collected.
// Presses POST /engine/scale. Answered points live in localStorage; the
// metadata persists with the history row (§3.7 v2.5), so a reload restores
// an UNANSWERED keyboard while the done-set keeps answered ones hidden.
// #4493: a press that settles the session-start gate can carry the held
// turn's replies — they land in the chat from here. #4497: recorded=false
// is a stale press — toast + hide; rendering itself is gated by the parent
// (only rows after the last user message show buttons).

import { useMemo, useState } from 'react'
import { chatApi } from '../../api/chat'
import { useMessagingStore } from '../../store/messagingStore'
import { useToastStore } from '../../store/toastStore'
import styles from './ScaleButtons.module.css'

const DONE_KEY = 'engine_scale_done'
// same collision rule as the TG keyboard (tg_main _SCALE_PRIORITY):
// one intensity row, session_start winning over the rare collision
// (before_practice is retired — #4492)
const INTENSITY_PRIORITY = ['session_start', 'after_practice']

function loadDone(): Set<string> {
  try {
    const raw = JSON.parse(localStorage.getItem(DONE_KEY) || '[]')
    return new Set(Array.isArray(raw) ? raw : [])
  } catch {
    return new Set()
  }
}

function markDone(key: string) {
  try {
    const done = loadDone()
    done.add(key)
    localStorage.setItem(DONE_KEY, JSON.stringify([...done]))
  } catch {
    // Private mode / storage disabled — the in-memory state still hides it.
  }
}

interface ScaleButtonsProps {
  points: string[]
  msgKey: string
}

export default function ScaleButtons({ points, msgKey }: ScaleButtonsProps) {
  const toast = useToastStore((s) => s.push)
  const addMessage = useMessagingStore((s) => s.addMessage)
  const [done, setDone] = useState<Set<string>>(() => loadDone())
  const [busy, setBusy] = useState(false)

  const blocks = useMemo(() => {
    // kind-scoped keys: after_practice has two independent blocks and each
    // must clear on its own answer
    const out: Array<{ point: string; kind: 'intensity' | 'usefulness' }> = []
    const intensity = INTENSITY_PRIORITY.find((p) => points.includes(p))
    if (intensity) out.push({ point: intensity, kind: 'intensity' })
    if (points.includes('after_practice')) out.push({ point: 'after_practice', kind: 'usefulness' })
    return out.filter((b) => !done.has(`${msgKey}:${b.kind}:${b.point}`))
  }, [points, msgKey, done])

  if (!blocks.length) return null

  const press = async (
    kind: 'intensity' | 'usefulness' | 'skip',
    point: string | undefined,
    value: number | undefined,
    doneKind: 'intensity' | 'usefulness'
  ) => {
    if (busy) return
    setBusy(true)
    try {
      const res = await chatApi.engineScale({ kind, point, value })
      markDone(`${msgKey}:${doneKind}:${point ?? 'after_practice'}`)
      setDone(loadDone())
      if (res.recorded === false) {
        // #4497: stale press — the value landed elsewhere or the point
        // retired; the keyboard goes away with this toast
        toast(res.message || 'Эта оценка уже неактуальна', 'info')
      } else {
        toast(res.message || 'Записано', 'success')
      }
      // #4493: the press may have released the gate-held turn
      for (const text of res.replies || []) addMessage('assistant', text)
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Не удалось записать ответ', 'error')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className={styles.wrap}>
      {blocks.map((b) =>
        b.kind === 'intensity' ? (
          <div key={`i-${b.point}`} className={styles.block}>
            <div className={styles.row}>
              {[1, 2, 3, 4, 5].map((v) => (
                <button key={v} className={styles.btn} disabled={busy} onClick={() => press('intensity', b.point, v, 'intensity')}>
                  {v}
                </button>
              ))}
            </div>
            <div className={styles.row}>
              {[6, 7, 8, 9, 10].map((v) => (
                <button key={v} className={styles.btn} disabled={busy} onClick={() => press('intensity', b.point, v, 'intensity')}>
                  {v}
                </button>
              ))}
            </div>
            <button className={styles.skip} disabled={busy} onClick={() => press('skip', b.point, undefined, 'intensity')}>
              Пропустить
            </button>
          </div>
        ) : (
          <div key={`u-${b.point}`} className={styles.block}>
            <div className={styles.row}>
              {[1, 2, 3, 4, 5].map((v) => (
                // usefulness records against the last skill_log row — the
                // wire carries no point (TG callback sc:u:<v> parity)
                <button key={v} className={styles.btn} disabled={busy} onClick={() => press('usefulness', undefined, v, 'usefulness')}>
                  ★{v}
                </button>
              ))}
            </div>
          </div>
        )
      )}
    </div>
  )
}
